import { useAuth0 } from '@auth0/auth0-react';
import DOMPurify from 'dompurify';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { OutreachEmailTemplate } from './DTO/OutreachEmailTemplate';
import { SendOutreachEmailsRequest, SendOutreachEmailsResponse } from './DTO/SendOutreachEmails';
import useRecruitmentId from '@/Hooks/useRecruitmentId';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import http from '@/Utils/HTTP/NewHttpUtils';

export const useOutreachEmailTemplate = () => {
    const { getAccessTokenSilently } = useAuth0();
    const recruitmentId = useRecruitmentId();

    return useSWR(`recruitment/${recruitmentId}/outreach-emails/template`, async (path) => {
        try {
            const response = await http.get<OutreachEmailTemplate>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            response.templateHtml = DOMPurify.sanitize(response.templateHtml);

            return response;
        } catch (error) {
            if (error instanceof ResourceNotFoundError) {
                return null;
            }

            throw error;
        }
    });
};

export const useSendOutreachEmail = () => {
    const { getAccessTokenSilently } = useAuth0();
    const recruitmentId = useRecruitmentId();

    return useSWRMutation(
        `recruitment/${recruitmentId}/outreach-emails/send`,
        async (path, { arg }: { arg: SendOutreachEmailsRequest }) => {
            const response = await http.post<SendOutreachEmailsResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
            return response;
        },
    );
};

export interface OutreachEmailThreadResponse {
    id: string;
    /**
     * Recruiter id who sent the email
     */
    recruiterId: string;
    shortlistedProfileId: string;

    createdAt: string;
    /**
     * Only count the emails sent via TalentMesh outreach or TalentMesh sequence
     */
    emailSentCount: number;
    lastEmailAt: string;
    repliedAt: string | null;
    /**
     * Collection of emails for this thread, ordered by MailServerReceivedAt
     */
    emails: Array<OutreachEmailResponse>;
}

export interface EmailParticipantResponse {
    email: string;
    name: string | null;
}

export interface OutreachEmailResponse {
    id: string; // Guid is typically represented as string in TypeScript

    subject: string;
    htmlBody: string;

    from: EmailParticipantResponse[] | null;
    to: EmailParticipantResponse[] | null;
    cc: EmailParticipantResponse[] | null;
    bcc: EmailParticipantResponse[] | null;

    /**
     * Represents when the mail server received the message
     */
    mailServerReceivedAt: string;

    repliedAt: string | null;
    replyMessageId: string | null;
    rootMessageId: string | null;
}

const DefaultOutreachEmailThreadResponse: OutreachEmailThreadResponse = {
    id: '',
    recruiterId: '',
    shortlistedProfileId: '',
    createdAt: '',
    emailSentCount: 0,
    lastEmailAt: '',
    repliedAt: null,
    emails: [],
};

export const getOutreachEmailThreadUrl = (recruitmentId: string, profileId: string) => {
    return `recruitment/${recruitmentId}/shortlisted-profile/${profileId}/outreach-email-thread`;
};

export const useOutreachEmailThread = (shortlistedProfileId: string) => {
    const recruitmentId = useRecruitmentId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWR(getOutreachEmailThreadUrl(recruitmentId, shortlistedProfileId), async (url: string) => {
        try {
            const response = await http.get<OutreachEmailThreadResponse>({
                path: url,
                accessToken: await getAccessTokenSilently(),
            });

            const sanitizedEmails = response.emails.map((email) => ({
                ...email,
                htmlBody: DOMPurify.sanitize(email.htmlBody),
            }));

            const safeResponse: OutreachEmailThreadResponse = { ...response, emails: sanitizedEmails };

            return safeResponse || DefaultOutreachEmailThreadResponse;
        } catch (error: any) {
            if (error instanceof ResourceNotFoundError) {
                return null;
            }

            throw error;
        }
    });
};

export interface GeneratedOutreachEmailResponse {
    emailSubject: string;
    emailMessageBody: string;
}

const DefaultGeneratedOutreachEmailResponse: GeneratedOutreachEmailResponse = {
    emailSubject: '',
    emailMessageBody: '',
};

export const useGenerateOutreach = () => {
    const recruitmentId = useRecruitmentId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWRMutation(`recruitment/${recruitmentId}/outreach-emails/generate`, async (path) => {
        const response = await http.post<GeneratedOutreachEmailResponse>({
            path,
            accessToken: await getAccessTokenSilently(),
        });

        return response || DefaultGeneratedOutreachEmailResponse;
    });
};

export interface LinkedInMessageOutreachResponse {
    subject: string;
    message: string;
}

export const useGenerateLinkedInMessageOutreach = () => {
    const recruitmentId = useRecruitmentId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWRMutation(`recruitment/${recruitmentId}/linkedin/message/generate`, async (path) => {
        return await http.post<LinkedInMessageOutreachResponse>({
            path,
            accessToken: await getAccessTokenSilently(),
        });
    });
};

export const useLinkedInMessageOutreach = () => {
    const recruitmentId = useRecruitmentId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWR(`recruitment/${recruitmentId}/linkedin/message`, async (path) => {
        try {
            const response = await http.get<LinkedInMessageOutreachResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        } catch (error: any) {
            if (error instanceof ResourceNotFoundError) {
                return null;
            }

            throw error;
        }
    });
};

type SaveLinkedInMessageOutreachRequest = LinkedInMessageOutreachResponse;

export const useSaveLinkedInMessageOutreach = () => {
    const recruitmentId = useRecruitmentId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWRMutation(
        `recruitment/${recruitmentId}/linkedin/message`,
        async (path, { arg }: { arg: SaveLinkedInMessageOutreachRequest }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
    );
};
